/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Modal, Result, Tabs, Tooltip } from 'antd';
import { ExclamationCircleOutlined, UserAddOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import RightSideContentWrapper from '../Wrappers/RightSideContentWrapper/RightSideContentWrapper';
import TenderDetails from '../../molecules/DetailsLayouts/TenderDetails';
import CachedPublicTenderContext, {
  StoredPublicTender,
} from '~src/contexts/CachedPublicTender';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { loadPublicTenderCoreRegistration } from '~src/services/api/shares';
import { FileTableProps } from '~components/atoms/FileTables/FileTable';
import { styleFn } from '~components/atoms/FileTables/FileTable.style';
import { PublicShareInviteModal } from '~components/popups-modals/PublicShareInvite.modal';

const { TabPane } = Tabs;

type PublicTenderUrlParams = {
  tenderShareId: string;
};

/** Public tender view */
export const PublicTenderAttachmentsTable: React.FC<
  FileTableProps & { resetSelectedForDetails: () => void }
> = ({ resetSelectedForDetails }) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();
  const { path, url } = useRouteMatch();

  const { tenderShareId } = useParams<PublicTenderUrlParams>();

  const [activeTab, setActiveTab] = useState<string | undefined>();
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);

  const {
    cachedPublicTendersContext,
    setCachedPublicTendersContext,
    currentCachedPublicTender,
    setCurrentCachedPublicTender,
  } = useContext(CachedPublicTenderContext);

  useEffect(() => {
    if (!tenderShareId) return;

    if (currentCachedPublicTender?.tenderShareId === tenderShareId) {
      return;
    }

    const publicTenderFromCache = cachedPublicTendersContext.find(
      (publicTender) => publicTender.tenderShareId === tenderShareId
    );

    if (publicTenderFromCache) {
      setCurrentCachedPublicTender({
        tenderShareId: publicTenderFromCache.tenderShareId,
      });
      return;
    }

    setCurrentCachedPublicTender({
      tenderShareId,
    });
  }, [
    tenderShareId,
    cachedPublicTendersContext,
    currentCachedPublicTender,
    setCurrentCachedPublicTender,
  ]);

  const handleAddNotListedCachedPublicTender = (
    newPublicTender: StoredPublicTender
  ) => {
    const storedPublicTenders: StoredPublicTender[] = [
      ...cachedPublicTendersContext,
    ];

    const existingPublicTenderIndex = storedPublicTenders.findIndex(
      (publicTender) =>
        publicTender.tenderShareId === newPublicTender.tenderShareId
    );

    if (existingPublicTenderIndex !== -1) {
      return;
    }

    storedPublicTenders.push(newPublicTender);

    setCachedPublicTendersContext(storedPublicTenders);
    localStorage.setItem('publicTenders', JSON.stringify(storedPublicTenders));
  };

  const removeCurrentCachedPublicTender = () => {
    if (!tenderShareId) return;

    const updatedCachedPublicTenders = cachedPublicTendersContext.filter(
      (publicTender) => publicTender.tenderShareId !== tenderShareId
    );

    setCachedPublicTendersContext(updatedCachedPublicTenders);
    localStorage.setItem(
      'publicTenders',
      JSON.stringify(updatedCachedPublicTenders)
    );

    setCurrentCachedPublicTender(null);
  };

  const handleRemoveFromCache = () => {
    setRemoveModalVisible(true);
  };

  const publicTenderQuery = useQuery(
    [QueryKeys.publicTender, { tenderShareId }],
    loadPublicTenderCoreRegistration,
    {
      enabled: !!tenderShareId,
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (publicTender) => {
        setCurrentCachedPublicTender({
          tenderShareId,
        });

        handleAddNotListedCachedPublicTender({
          tenderShareId,
          title: publicTender.share_title || publicTender.title,
          description: publicTender.description,
          active_until: publicTender.active_until,
          deadline_at: publicTender.deadline_at,
          global_serial: publicTender.global_serial,
        });
      },
    }
  );

  // Same default-tab logic as invitations.
  useEffect(() => {
    if (!tenderShareId) return;

    const pathSegments = history.location.pathname.split('/').filter(Boolean);

    const directMapping = {
      details: 'details',
      documents: 'documents',
    };

    setActiveTab((prevActiveTab) => {
      const currentActiveTab = pathSegments.find((segment) => {
        return Object.values(directMapping).includes(segment);
      });

      if (!currentActiveTab) {
        handleTabChange(directMapping.details);
      }

      return currentActiveTab || prevActiveTab;
    });
  }, [history.location.pathname, tenderShareId]); // eslint-disable-line react-hooks/exhaustive-deps

  const publicTenderState = Number(publicTenderQuery.data?.state) || 0;
  const isInviteSupplierDisabled =
    !tenderShareId || publicTenderQuery.isLoading || publicTenderState > 6;

  const inviteSupplierTooltip =
    publicTenderState > 6
      ? intl.formatMessage({
          id: 'public-tender-invite-supplier-disabled-tooltip',
        })
      : intl.formatMessage({
          id: 'public-tender-invite-supplier-tooltip',
        });

  const handleTabChange = (key: string) => {
    resetSelectedForDetails();
    setActiveTab(key);

    const baseUrl = url.split('?')[0].replace(/\/(details|documents)$/, '');
    history.replace(`${baseUrl}/${key}`);
  };

  const publicTenderHttpCode: number | undefined =
    (publicTenderQuery.error as any)?.response?.status || undefined;

  const isPublicTenderForbidden = publicTenderHttpCode === 403;

  if (publicTenderQuery.isError) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
        }}>
        <div style={{ width: '100%' }}>
          <Result
            status="error"
            title={intl.formatMessage({ id: 'public-tender-not-found' })}
            subTitle={intl.formatMessage({
              id: 'public-tender-not-found-info',
            })}
          />
        </div>
        <div
          css={styles.actionsContainer}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="primary" danger onClick={handleRemoveFromCache}>
            {intl.formatMessage({ id: 'cached-share-action-remove' })}
          </Button>
        </div>

        <Modal
          visible={removeModalVisible}
          onCancel={() => setRemoveModalVisible(false)}
          footer={null}
          centered
          width={440}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined
                style={{ color: '#faad14' }}
                rev={''}
              />
              {intl.formatMessage({
                id: 'cached-public-tender-remove-from-list-title',
              })}
            </div>
          }>
          <div style={{ marginBottom: 24 }}>
            {intl.formatMessage({
              id: 'cached-public-tender-remove-from-list-description',
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setRemoveModalVisible(false)}>
              {intl.formatMessage({ id: 'close' })}
            </Button>

            <Button
              type="primary"
              onClick={() => {
                removeCurrentCachedPublicTender();
                history.push('/public-tenders');
              }}>
              {intl.formatMessage({
                id: 'cached-public-tender-remove-from-list-ok',
              })}
            </Button>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        position: 'relative',
      }}>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        defaultActiveKey="details"
        style={{ marginTop: '-2px', width: '100%' }}>
        <TabPane
          tab={
            <div style={{ paddingLeft: '8px' }}>
              {intl.formatMessage({ id: 'tab-details' })}
            </div>
          }
          key="details">
          <Switch>
            <Route path={`${path}/details`}>
              <RightSideContentWrapper>
                <TenderDetails
                  tenderData={publicTenderQuery.data}
                  tenderId={tenderShareId}
                  isPublicTender
                />
              </RightSideContentWrapper>
            </Route>
          </Switch>
        </TabPane>

        {/* Future public tender tabs can be added here */}
      </Tabs>

      <div
        css={styles.actionsContainer}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* 1. Remove */}
        <Tooltip
          title={intl.formatMessage({
            id: 'cached-public-tender-action-remove-tooltip',
          })}>
          <Button onClick={handleRemoveFromCache}>
            {intl.formatMessage({ id: 'cached-share-action-remove' })}
          </Button>
        </Tooltip>

        {/* 2. Invite supplier */}
        <Tooltip title={inviteSupplierTooltip}>
          <span>
            <Button
              type="primary"
              icon={<UserAddOutlined rev={''} />}
              disabled={isInviteSupplierDisabled}
              onClick={() => setInviteModalVisible(true)}>
              {intl.formatMessage({ id: 'public-tender-invite-supplier' })}
            </Button>
          </span>
        </Tooltip>
        {/* Future public tender actions can be added here */}
      </div>

      <Modal
        visible={removeModalVisible}
        onCancel={() => setRemoveModalVisible(false)}
        footer={null}
        centered
        width={440}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} rev={''} />
            {intl.formatMessage({
              id: 'cached-public-tender-remove-from-list-title',
            })}
          </div>
        }>
        <div style={{ marginBottom: 24 }}>
          {intl.formatMessage({
            id: 'cached-public-tender-remove-from-list-description',
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => setRemoveModalVisible(false)}>
            {intl.formatMessage({ id: 'close' })}
          </Button>

          <Button
            type="primary"
            onClick={() => {
              removeCurrentCachedPublicTender();
              history.push('/public-tenders');
            }}>
            {intl.formatMessage({
              id: 'cached-public-tender-remove-from-list-ok',
            })}
          </Button>
        </div>
      </Modal>

      <PublicShareInviteModal
        visible={inviteModalVisible}
        tenderShareId={tenderShareId}
        tenderData={publicTenderQuery.data}
        onClose={() => setInviteModalVisible(false)}
      />
    </div>
  );
};
